import { getAccessToken } from "./auth.js";
import { config } from "./config.js";
import { driveItemPath } from "./paths.js";

const GRAPH = "https://graph.microsoft.com/v1.0";

let resolvedFolder = null; // e.g. "Projeler/PaperReader"

async function graphFetch(path, options = {}) {
  const token = await getAccessToken();
  if (!token) throw new Error("No access token");
  const res = await fetch(`${GRAPH}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body && !(options.body instanceof ArrayBuffer)
        ? { "Content-Type": "application/json" }
        : {}),
      ...options.headers,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Graph ${res.status}: ${text.slice(0, 400)}`);
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

function encodeDrivePath(path) {
  return path
    .split("/")
    .filter(Boolean)
    .map((p) => encodeURIComponent(p))
    .join("/");
}

export function getResolvedFolder() {
  return resolvedFolder || config.paperReaderFolder;
}

async function readJsonAtFolder(folder, relativeName) {
  const full = driveItemPath(folder, relativeName);
  const url = `/me/drive/root:/${encodeDrivePath(full)}:/content`;
  try {
    const text = await graphFetch(url);
    if (!text || typeof text !== "string") return null;
    return JSON.parse(text);
  } catch (err) {
    if (String(err.message).includes("404")) return null;
    throw err;
  }
}

async function searchMyLibraryCandidates() {
  // Search entire drive for library file
  const data = await graphFetch(
    `/me/drive/root/search(q='my_library.json')?$select=id,name,parentReference,webUrl`
  );
  const hits = (data?.value || []).filter((x) => x.name === "my_library.json");
  return hits.map((h) => {
    // parentReference.path like "/drive/root:/Projeler/PaperReader"
    const path = h.parentReference?.path || "";
    const idx = path.indexOf(":");
    const folder = idx >= 0 ? path.slice(idx + 1).replace(/^\/+/, "") : "";
    return { folder, webUrl: h.webUrl, id: h.id };
  }).filter((x) => x.folder);
}

export async function ensurePaperReaderFolder() {
  const preferred = config.paperReaderFolder.replace(/^\/+|\/+$/g, "");
  const tryFolders = [preferred, "PaperReader", "Documents/PaperReader"];

  for (const folder of tryFolders) {
    try {
      await graphFetch(`/me/drive/root:/${encodeDrivePath(folder)}:`);
      const lib = await readJsonAtFolder(folder, "my_library.json");
      const count = Array.isArray(lib?.papers) ? lib.papers.length : 0;
      if (count > 0 || folder === preferred) {
        resolvedFolder = folder;
        // Prefer a folder that actually has papers
        if (count > 0) return { folder, paperCount: count };
      }
    } catch (err) {
      if (!String(err.message).includes("404")) throw err;
    }
  }

  // Fallback: search drive
  const found = await searchMyLibraryCandidates();
  for (const cand of found) {
    const lib = await readJsonAtFolder(cand.folder, "my_library.json");
    const count = Array.isArray(lib?.papers) ? lib.papers.length : 0;
    if (count > 0) {
      resolvedFolder = cand.folder;
      return { folder: cand.folder, paperCount: count };
    }
  }

  if (found.length) {
    resolvedFolder = found[0].folder;
    return { folder: found[0].folder, paperCount: 0, searched: true };
  }

  // Last resort: keep preferred even if missing (clearer error later)
  resolvedFolder = preferred;
  try {
    await graphFetch(`/me/drive/root:/${encodeDrivePath(preferred)}:`);
    return { folder: preferred, paperCount: 0 };
  } catch (err) {
    if (String(err.message).includes("404")) {
      throw new Error(
        `Bu Microsoft hesabının OneDrive’ında «${preferred}» yok.\n\n` +
          `Giriş yaptığın hesap, Mac’teki OneDrive-Personal ile aynı olmalı.\n` +
          `Üstteki e-postayı kontrol et; yanlışsa Çıkış yapıp Personal hesabıyla gir.`
      );
    }
    throw err;
  }
}

export async function whoAmI() {
  try {
    return await graphFetch("/me?$select=displayName,mail,userPrincipalName");
  } catch {
    return null;
  }
}

export async function readJsonFile(relativeName) {
  return readJsonAtFolder(getResolvedFolder(), relativeName);
}

export async function writeJsonFile(relativeName, data) {
  const full = driveItemPath(getResolvedFolder(), relativeName);
  const url = `/me/drive/root:/${encodeDrivePath(full)}:/content`;
  const body = JSON.stringify(data, null, 2);
  const token = await getAccessToken();
  const res = await fetch(`${GRAPH}${url}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Upload failed ${res.status}: ${text.slice(0, 280)}`);
  }
  return res.json();
}

/** Push Mac local JSON files into the signed-in OneDrive (fixes empty-cloud). */
export async function syncLocalFilesToCloud() {
  resolvedFolder = config.paperReaderFolder.replace(/^\/+|\/+$/g, "");
  const names = ["my_library.json", "my_notebook.json", "my_research_notebook.json"];
  const results = [];
  for (const name of names) {
    const res = await fetch(`/local-data/${name}`, { cache: "no-store" });
    if (!res.ok) {
      results.push({ name, ok: false, reason: `local ${res.status}` });
      continue;
    }
    const data = await res.json();
    await writeJsonFile(name, data);
    const count =
      name === "my_library.json"
        ? (data.papers || []).length
        : name === "my_notebook.json"
          ? (data.entries || []).length
          : (data.notes || []).length;
    results.push({ name, ok: true, count });
  }
  return results;
}

export async function readTextFile(relativePath) {
  const full = driveItemPath(getResolvedFolder(), relativePath);
  const url = `/me/drive/root:/${encodeDrivePath(full)}:/content`;
  try {
    return await graphFetch(url);
  } catch (err) {
    if (String(err.message).includes("404")) return null;
    throw err;
  }
}

export async function loadLibrary() {
  // When Vite runs on this Mac, /local-data serves the real folder (also for phone on Wi‑Fi).
  try {
    const res = await fetch("/local-data/my_library.json", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      const papers = Array.isArray(data.papers) ? data.papers : [];
      if (papers.length) return papers;
    }
  } catch {
    /* fall through to Graph */
  }
  const data = (await readJsonFile("my_library.json")) || { papers: [] };
  return Array.isArray(data.papers) ? data.papers : [];
}

function isLocalHost() {
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1";
}

/** True when this page is served by the Mac Vite server (LAN phone included). */
export async function localDataAvailable() {
  try {
    const res = await fetch("/local-diagnose", { cache: "no-store" });
    if (!res.ok) return false;
    const d = await res.json();
    return Boolean(d?.exists && d?.papers > 0);
  } catch {
    return false;
  }
}

export async function diagnoseCloud() {
  const out = {
    me: null,
    root: [],
    projeler: null,
    preferred: null,
    search: [],
    local: null,
  };
  try {
    out.me = await whoAmI();
  } catch (e) {
    out.me = { error: String(e.message || e) };
  }
  try {
    const root = await graphFetch("/me/drive/root/children?$select=name,folder,size");
    out.root = (root?.value || []).map((x) => x.name);
  } catch (e) {
    out.root = [`ERROR ${e.message}`];
  }
  try {
    const p = await graphFetch(
      `/me/drive/root:/${encodeDrivePath("Projeler")}:/children?$select=name,folder,size`
    );
    out.projeler = (p?.value || []).map((x) => x.name);
  } catch (e) {
    out.projeler = `ERROR ${e.message}`;
  }
  try {
    const folder = config.paperReaderFolder;
    const meta = await graphFetch(
      `/me/drive/root:/${encodeDrivePath(folder + "/my_library.json")}:?$select=name,size,lastModifiedDateTime`
    );
    out.preferred = meta;
  } catch (e) {
    out.preferred = `ERROR ${e.message}`;
  }
  try {
    out.search = await searchMyLibraryCandidates();
  } catch (e) {
    out.search = [{ error: String(e.message || e) }];
  }
  if (isLocalHost()) {
    try {
      out.local = await (await fetch("/local-diagnose")).json();
    } catch (e) {
      out.local = { error: String(e.message || e) };
    }
  }
  return out;
}

export async function saveLibrary(papers) {
  await writeJsonFile("my_library.json", { papers });
}

export async function loadResearchNotes() {
  try {
    const res = await fetch("/local-data/my_research_notebook.json", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      const notes = Array.isArray(data.notes) ? data.notes : [];
      return notes.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    }
  } catch {
    /* Graph fallback */
  }
  const data = (await readJsonFile("my_research_notebook.json")) || { notes: [] };
  const notes = Array.isArray(data.notes) ? data.notes : [];
  return notes.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
}

export async function saveResearchNotes(notes) {
  await writeJsonFile("my_research_notebook.json", { notes });
}

export async function loadPaperNotebook() {
  try {
    const res = await fetch("/local-data/my_notebook.json", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      const entries = Array.isArray(data.entries) ? data.entries : [];
      return entries.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
    }
  } catch {
    /* Graph fallback */
  }
  const data = (await readJsonFile("my_notebook.json")) || { entries: [] };
  const entries = Array.isArray(data.entries) ? data.entries : [];
  return entries.sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""));
}

export async function savePaperNotebook(entries) {
  await writeJsonFile("my_notebook.json", { entries });
}
